import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { auth, db, messaging } from '../constants/firebase';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { createLogger } from '../utils/logger';

const logger = createLogger('FCMService');

// FCM configuration
const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY || 'your-vapid-key-here';

// localStorage keys for fallback token storage
const FCM_TOKEN_STORAGE_KEY = 'fcm_token_fallback';
const FCM_TOKEN_TIMESTAMP_KEY = 'fcm_token_timestamp';

class FCMService {
  private isSupported: boolean = false;

  constructor() {
    this.isSupported = messaging !== null;
    if (this.isSupported) {
      this.setupMessageListener();
    } else {
      logger.warn('Firebase Messaging not supported in this browser');
    }
  }

  /**
   * Check if FCM is supported in this browser
   */
  isFCMSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Store FCM token in localStorage as fallback
   */
  private storeTokenInLocalStorage(token: string): void {
    try {
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
      localStorage.setItem(FCM_TOKEN_TIMESTAMP_KEY, Date.now().toString());
      logger.debug('FCM token stored in localStorage as fallback');
    } catch (error) {
      logger.warn('Failed to store FCM token in localStorage:', error);
    }
  }

  /**
   * Get FCM token from localStorage fallback
   */
  private getTokenFromLocalStorage(): string | null {
    try {
      const token = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
      const timestamp = localStorage.getItem(FCM_TOKEN_TIMESTAMP_KEY);
      
      if (!token) {
        return null;
      }

      // Check if token is not too old (30 days)
      if (timestamp) {
        const tokenAge = Date.now() - parseInt(timestamp, 10);
        const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
        if (tokenAge > thirtyDaysInMs) {
          logger.warn('Stored FCM token is too old, removing from localStorage');
          this.clearTokenFromLocalStorage();
          return null;
        }
      }

      logger.debug('FCM token retrieved from localStorage fallback');
      return token;
    } catch (error) {
      logger.warn('Failed to get FCM token from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear FCM token from localStorage
   */
  private clearTokenFromLocalStorage(): void {
    try {
      localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
      localStorage.removeItem(FCM_TOKEN_TIMESTAMP_KEY);
      logger.debug('FCM token cleared from localStorage');
    } catch (error) {
      logger.warn('Failed to clear FCM token from localStorage:', error);
    }
  }

  /**
   * Get current FCM token if available
   * Falls back to localStorage if IndexedDB fails
   */
  async getCurrentToken(): Promise<string | null> {
    if (!this.isSupported || !messaging) {
      logger.warn('FCM not supported or messaging not available');
      // Try localStorage fallback
      return this.getTokenFromLocalStorage();
    }

    try {
      // Check notification permission before attempting to get token
      if (Notification.permission === 'denied') {
        logger.warn('Notification permission denied - cannot get FCM token');
        // Try localStorage fallback
        return this.getTokenFromLocalStorage();
      }

      // Quick IndexedDB availability check (non-blocking)
      const indexedDBAvailable = this.isIndexedDBAvailable();
      if (!indexedDBAvailable) {
        logger.warn('IndexedDB not available - trying localStorage fallback');
        const fallbackToken = this.getTokenFromLocalStorage();
        if (fallbackToken) {
          logger.info('Using FCM token from localStorage fallback');
          return fallbackToken;
        }
        // Still try to get token as Firebase might handle it internally
      }

      logger.debug('Attempting to get FCM token...');
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
      });
      
      if (token) {
        logger.debug('FCM token obtained successfully');
        // Store in localStorage as backup
        this.storeTokenInLocalStorage(token);
        return token;
      } else {
        logger.warn('FCM token is null, trying localStorage fallback');
        return this.getTokenFromLocalStorage();
      }
    } catch (error) {
      logger.error('Error getting current FCM token:', error);
      
      // Try localStorage fallback before throwing error
      const fallbackToken = this.getTokenFromLocalStorage();
      if (fallbackToken) {
        logger.info('Using FCM token from localStorage fallback after error');
        return fallbackToken;
      }
      
      // Provide specific error messages for different failure types
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        if (errorMessage.includes('permission-blocked') || errorMessage.includes('permission denied')) {
          throw new Error('Notification permission blocked by browser');
        } else if (errorMessage.includes('indexeddb') || errorMessage.includes('quota') || errorMessage.includes('storage')) {
          // Check if we have a localStorage fallback
          const fallbackToken = this.getTokenFromLocalStorage();
          if (fallbackToken) {
            logger.info('IndexedDB failed but localStorage fallback available');
            return fallbackToken;
          }
          throw new Error('IndexedDB not functional - using localStorage fallback');
        } else if (errorMessage.includes('unsupported-browser') || errorMessage.includes('not supported')) {
          throw new Error('Browser does not support Firebase Messaging');
        } else {
          throw new Error(`FCM token generation failed: ${error.message}`);
        }
      }
      
      throw new Error('FCM token generation failed due to unknown error');
    }
  }

  /**
   * Detect platform (web, android, ios)
   */
  private detectPlatform(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('android')) return 'android';
    if (ua.includes('iphone') || ua.includes('ipad')) return 'ios';
    return 'web';
  }

  /**
   * Check if IndexedDB is available and working
   */
  private isIndexedDBAvailable(): boolean {
    try {
      if (!('indexedDB' in window)) {
        logger.warn('IndexedDB not supported in this browser');
        return false;
      }
      
      // Basic IndexedDB availability check
      return true;
    } catch (error) {
      logger.error('IndexedDB check failed:', error);
      return false;
    }
  }

  /**
   * Test IndexedDB functionality asynchronously
   * Simplified test that just checks if we can open a database
   */
  private async testIndexedDB(): Promise<boolean> {
    try {
      logger.debug('Testing IndexedDB functionality...');
      
      // Check basic availability first
      if (!('indexedDB' in window) || !window.indexedDB) {
        logger.warn('IndexedDB not available in window');
        return false;
      }

      // Use a unique database name with timestamp to avoid conflicts
      const dbName = `fcm-test-db-${Date.now()}`;
      const testRequest = indexedDB.open(dbName, 1);
      
      return new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          logger.warn('IndexedDB test timed out after 3 seconds');
          resolve(false);
        }, 3000); // Reduced to 3 second timeout for faster failure
        
        testRequest.onerror = (event) => {
          clearTimeout(timeout);
          // Extract meaningful error information from the event
          try {
            const target = event.target as IDBOpenDBRequest;
            const error = target?.error;
            if (error) {
              const errorMessage = error.message || 'Unknown IndexedDB error';
              const errorName = error.name || 'UnknownError';
              logger.warn(`IndexedDB test failed: ${errorName}: ${errorMessage}`);
            } else {
              logger.warn('IndexedDB test failed: No error details available');
            }
          } catch (parseError) {
            logger.warn('IndexedDB test failed: Could not parse error details');
          }
          resolve(false);
        };
        
        testRequest.onupgradeneeded = () => {
          // Database upgrade needed - this is normal for new databases
          // Don't create object stores to keep test simple
        };
        
        testRequest.onsuccess = () => {
          clearTimeout(timeout);
          try {
            const db = testRequest.result;
            if (!db) {
              logger.warn('IndexedDB test failed: database result is null');
              resolve(false);
              return;
            }
            
            // Just verify we can access the database, then close it
            db.close();
            
            // Clean up test database (non-blocking)
            try {
              const deleteRequest = indexedDB.deleteDatabase(dbName);
              deleteRequest.onsuccess = () => {
                logger.debug('IndexedDB test successful');
              };
              deleteRequest.onerror = () => {
                logger.debug('IndexedDB test successful (cleanup failed, but database opened)');
              };
            } catch (deleteError) {
              // Ignore cleanup errors
            }
            
            resolve(true);
          } catch (error) {
            logger.warn('IndexedDB test error during success handler', error);
            resolve(false);
          }
        };
        
        testRequest.onblocked = () => {
          clearTimeout(timeout);
          logger.warn('IndexedDB blocked - may be in use by another tab');
          // Consider it working if blocked (another tab has it open)
          resolve(true);
        };
      });
    } catch (error) {
      logger.warn('IndexedDB test error', error);
      return false;
    }
  }

  /**
   * Get detailed FCM diagnostics for troubleshooting
   */
  async getFCMDiagnostics(): Promise<{
    supported: boolean;
    browserCompatible: boolean;
    indexedDBAvailable: boolean;
    indexedDBWorking: boolean;
    serviceWorkerRegistered: boolean;
    notificationPermission: string;
    error?: string;
  }> {
    const diagnostics = {
      supported: this.isSupported,
      browserCompatible: false,
      indexedDBAvailable: false,
      indexedDBWorking: false,
      serviceWorkerRegistered: false,
      notificationPermission: Notification.permission,
      error: undefined as string | undefined
    };

    try {
      // Check browser compatibility
      diagnostics.browserCompatible = (
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window &&
        window.location.protocol === 'https:'
      );

      // Check IndexedDB availability
      diagnostics.indexedDBAvailable = this.isIndexedDBAvailable();

      // Test IndexedDB functionality
      if (diagnostics.indexedDBAvailable) {
        diagnostics.indexedDBWorking = await this.testIndexedDB();
      }

      // Check service worker registration (root or push-scope, both valid)
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        diagnostics.serviceWorkerRegistered = registrations.some(reg => 
          reg.active?.scriptURL?.includes('firebase-messaging-sw.js')
        );
      } catch (swError) {
        logger.warn('Could not check service worker registrations:', swError);
      }

      // Try to get current token to detect specific issues
      if (diagnostics.supported && messaging) {
        try {
          const token = await this.getCurrentToken();
          if (!token && Notification.permission === 'granted') {
            diagnostics.error = 'FCM token generation failed despite granted permission';
          }
        } catch (error) {
          diagnostics.error = error instanceof Error ? error.message : 'Unknown FCM error';
        }
      }

    } catch (error) {
      diagnostics.error = error instanceof Error ? error.message : 'Diagnostic check failed';
    }

    return diagnostics;
  }

  /**
   * Clear FCM-related storage to resolve IndexedDB issues
   */
  private async clearFCMStorage(): Promise<void> {
    try {
      // Clear localStorage FCM data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('fcm') || key.includes('firebase'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage FCM data
      const sessionKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.includes('fcm') || key.includes('firebase'))) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));

      // Clear IndexedDB FCM databases
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          for (const db of databases) {
            if (db.name && (db.name.includes('fcm') || db.name.includes('firebase'))) {
              await indexedDB.deleteDatabase(db.name);
            }
          }
        } catch (dbError) {
          logger.warn('Could not clear IndexedDB databases:', dbError);
        }
      }

      logger.info('FCM storage cleared successfully');
    } catch (error) {
      logger.error('Error clearing FCM storage:', error);
    }
  }

  /**
   * Get FCM token with retry mechanism for IndexedDB issues
   * @param serviceWorkerRegistration - SW registration to bind token to (ensures FCM delivers to our SW)
   */
  private async getTokenWithRetry(
    serviceWorkerRegistration?: ServiceWorkerRegistration,
    maxRetries: number = 3
  ): Promise<string | null> {
    if (!messaging) {
      logger.error('Firebase messaging not available');
      return null;
    }

    // First, do a quick IndexedDB availability check (non-blocking)
    const indexedDBAvailable = this.isIndexedDBAvailable();
    if (!indexedDBAvailable) {
      logger.warn('IndexedDB not available - FCM may not work');
      // Still try to get token as Firebase might handle it
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger.debug(`FCM token attempt ${attempt}/${maxRetries}`);
        
        // Only test IndexedDB on first attempt to avoid unnecessary delays
        if (attempt === 1 && indexedDBAvailable) {
          const indexedDBWorking = await this.testIndexedDB();
          if (!indexedDBWorking) {
            logger.warn('IndexedDB test failed, but attempting token generation anyway');
            // Try clearing storage on first failure
            try {
              await this.clearFCMStorage();
              await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
            } catch (clearError) {
              logger.warn('Could not clear FCM storage:', clearError);
            }
          }
        }
        
        // Attempt to get token - pass SW registration so token is bound to our SW for delivery
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          ...(serviceWorkerRegistration && { serviceWorkerRegistration })
        });
        
        if (token) {
          logger.debug(`FCM token obtained on attempt ${attempt}`);
          // Store in localStorage as backup
          this.storeTokenInLocalStorage(token);
          return token;
        }
        
        // If token is null, try localStorage fallback
        const fallbackToken = this.getTokenFromLocalStorage();
        if (fallbackToken) {
          logger.info('Using FCM token from localStorage fallback');
          return fallbackToken;
        }
        
        if (attempt < maxRetries) {
          logger.warn(`FCM token attempt ${attempt} returned null, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error(`FCM token attempt ${attempt} error:`, error);
        
        // Check for specific error types
        if (errorMessage.includes('indexedDB') || errorMessage.includes('IndexedDB')) {
          // Try localStorage fallback first
          const fallbackToken = this.getTokenFromLocalStorage();
          if (fallbackToken) {
            logger.info('Using FCM token from localStorage fallback after IndexedDB error');
            return fallbackToken;
          }
          
          // On first attempt with IndexedDB error, try clearing storage and retry once
          if (attempt === 1) {
            logger.warn('IndexedDB error detected, attempting to clear storage and retry...');
            try {
              await this.clearFCMStorage();
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue; // Retry immediately after clearing
            } catch (clearError) {
              logger.warn('Could not clear storage, continuing with retry');
            }
          }
          
          // If still failing after retries, try localStorage one more time
          if (attempt >= maxRetries) {
            const finalFallback = this.getTokenFromLocalStorage();
            if (finalFallback) {
              logger.info('Using FCM token from localStorage fallback after all retries failed');
              return finalFallback;
            }
            logger.error('IndexedDB errors persist after all retries and no localStorage fallback available');
            break;
          }
        }
        
        // For other errors, retry with backoff
        if (attempt < maxRetries) {
          logger.warn(`Retrying in ${1000 * attempt}ms...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    
    // Final attempt: try localStorage fallback
    const finalFallback = this.getTokenFromLocalStorage();
    if (finalFallback) {
      logger.info('Using FCM token from localStorage fallback after all attempts failed');
      return finalFallback;
    }
    
    logger.error('All FCM token attempts failed and no localStorage fallback available');
    return null;
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermissionAndGetToken(): Promise<string | null> {
    if (!this.isSupported || !messaging) {
      logger.warn('Messaging not supported');
      return null;
    }

    try {
      // Unregister legacy push-scope SW to avoid token/SW mismatch
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        if (reg.scope.includes('firebase-cloud-messaging-push-scope')) {
          await reg.unregister();
          logger.info('Unregistered legacy FCM SW (push-scope)');
          break;
        }
      }
      // Register FCM SW at ROOT scope so it controls all pages and receives background messages
      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/'
      });
      await navigator.serviceWorker.ready; // Ensure SW is active before getToken

      // Check if permission is already granted
      if (Notification.permission === 'granted') {
        logger.debug('Notification permission already granted');
      } else if (Notification.permission === 'denied') {
        logger.warn('Notification permission denied by user');
        return null;
      } else {
        // Request notification permission
        logger.debug('Requesting notification permission...');
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          logger.warn('Notification permission denied by user');
          return null;
        }
        logger.debug('Notification permission granted');
      }

      // Get FCM token, passing our SW registration so token is bound to the correct SW
      logger.debug('Attempting to get FCM token with retry mechanism...');
      const token = await this.getTokenWithRetry(swReg);

      if (token) {
        // Validate token format (FCM tokens are typically 150-160 characters)
        if (token.length < 100 || token.length > 200) {
          logger.error('Invalid FCM token format', { length: token.length });
          // Try localStorage fallback
          const fallbackToken = this.getTokenFromLocalStorage();
          if (fallbackToken && fallbackToken.length >= 100 && fallbackToken.length <= 200) {
            logger.info('Using valid FCM token from localStorage fallback');
            await this.saveTokenToFirestore(fallbackToken);
            return fallbackToken;
          }
          return null;
        }
        
        logger.debug('FCM token obtained, length:', token.length);
        // Store in localStorage as backup
        this.storeTokenInLocalStorage(token);
        await this.saveTokenToFirestore(token);
        return token;
      } else {
        // Try localStorage fallback
        const fallbackToken = this.getTokenFromLocalStorage();
        if (fallbackToken) {
          logger.info('Using FCM token from localStorage fallback');
          await this.saveTokenToFirestore(fallbackToken);
          return fallbackToken;
        }
        logger.warn('No registration token available');
        return null;
      }
    } catch (error) {
      logger.error('Error getting FCM token', error);
      
      // If IndexedDB error, try localStorage fallback
      if (error instanceof Error && error.message && error.message.includes('indexedDB')) {
        logger.warn('IndexedDB error detected. Trying localStorage fallback...');
        
        // Try localStorage fallback first
        const fallbackToken = this.getTokenFromLocalStorage();
        if (fallbackToken) {
          logger.info('Using FCM token from localStorage fallback');
          await this.saveTokenToFirestore(fallbackToken);
          return fallbackToken;
        }
        
        // Try fallback: clear storage and retry once
        try {
          await this.clearFCMStorage();
          logger.info('FCM storage cleared, retrying token generation...');
          
          if (messaging) {
            const regs = await navigator.serviceWorker.getRegistrations();
            const fcmReg = regs.find(r => r.active?.scriptURL?.includes('firebase-messaging-sw'));
            const retryToken = await getToken(messaging, {
              vapidKey: VAPID_KEY,
              ...(fcmReg && { serviceWorkerRegistration: fcmReg })
            });
            
            if (retryToken) {
              logger.info('FCM token obtained after storage clear');
              this.storeTokenInLocalStorage(retryToken);
              await this.saveTokenToFirestore(retryToken);
              return retryToken;
            }
          }
        } catch (fallbackError) {
          logger.error('Fallback method also failed:', fallbackError);
        }
        
        logger.warn('IndexedDB error - using localStorage fallback. Token may need to be refreshed periodically.');
      }
      
      return null;
    }
  }

  /**
   * Save FCM token to user's Firestore document
   * Simplified to use consistent lookup strategy
   */
  private async saveTokenToFirestore(token: string) {
    try {
      const user = auth.currentUser;
      if (!user) {
        logger.warn('No authenticated user to save token');
        return;
      }

      logger.debug(`Attempting to save FCM token for user: ${user.uid} ${user.email || ''}`);

      const deviceType = this.detectPlatform();
      const tokenData = {
        fcmToken: token,
        fcmTokenUpdatedAt: new Date(),
        deviceType: deviceType,
        userAgent: navigator.userAgent
      };

      // First, try to find the user document by identity_id
      const usersRef = collection(db, 'Users');
      const userQuery = query(usersRef, where('identity_id', '==', user.uid));
      const querySnapshot = await getDocs(userQuery);

      if (!querySnapshot.empty) {
        // User found by identity_id, update that document
        const userDoc = querySnapshot.docs[0];
        logger.debug('Found user document by identity_id:', userDoc.id);
        await setDoc(doc(db, 'Users', userDoc.id), tokenData, { merge: true });
        logger.debug('FCM token saved to existing user document');
        return;
      }

      // User not found by identity_id, check by email if available
      if (user.email) {
        const emailQuery = query(usersRef, where('email', '==', user.email));
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          // User found by email, update that document
          const userDoc = emailSnapshot.docs[0];
          logger.debug('Found user document by email:', userDoc.id);
          await setDoc(doc(db, 'Users', userDoc.id), tokenData, { merge: true });
          logger.debug('FCM token saved to existing user document found by email');
          return;
        }
      }

      // If no user document found, try using UID as document ID
      // Note: This requires that the user document already exists
      logger.warn('No user document found by identity_id or email');
      logger.warn('User document must be created before FCM token can be saved');
      
      // As a last resort, try to save with UID as document ID
      try {
        logger.debug('Attempting to save with UID as document ID as fallback');
        await setDoc(doc(db, 'Users', user.uid), tokenData, { merge: true });
        logger.debug('FCM token saved using UID as document ID');
      } catch (uidError) {
        logger.error('Failed to save FCM token - user document does not exist:', uidError);
        throw new Error('User document must exist before FCM token can be saved');
      }
    } catch (error) {
      logger.error('Error saving FCM token', error);
    }
  }

  /**
   * Setup listener for incoming FCM messages
   */
  private setupMessageListener() {
    if (!messaging) return;

    try {
      onMessage(messaging, (payload) => {
        logger.debug('Message received', payload);
        
        // Show notification
        this.showNotification(payload);
      });
    } catch (error) {
      logger.error('Failed to setup message listener', error);
    }
  }

  /**
   * Show browser notification
   */
  private async showNotification(payload: any) {
    const { notification, data } = payload;
    
    if (Notification.permission === 'granted') {
      // Use Service Worker to show notification with actions
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          
          const notificationOptions = {
            body: notification?.body || data?.body,
            icon: '/logo192.png',
            badge: '/logo192.png',
            // Remove tag to prevent collapsing/replacing foreground notifications
            requireInteraction: true,
            renotify: true,
            silent: false,
            vibrate: [100, 50, 100],
            timestamp: Date.now(),
            actions: [
              {
                action: 'view',
                title: 'View Order',
                icon: '/logo192.png'
              },
              {
                action: 'dismiss',
                title: 'Dismiss'
              }
            ],
            data: {
              click_action: data?.click_action || `${window.location.origin  }/`,
              orderId: data?.orderId,
              type: data?.type
            }
          };

          logger.debug('Showing notification via SW', {
            title: notification?.title || data?.title || 'PackMyCart',
            options: notificationOptions
          });

          await registration.showNotification(
            notification?.title || data?.title || 'PackMyCart',
            notificationOptions
          );

          logger.debug('Notification shown (SW)');
          
          return;
        } catch (swError) {
          logger.warn('Service Worker notification failed, falling back to basic notification', swError);
        }
      }
      
      // Fallback to basic notification without actions
      logger.debug('Showing basic notification (fallback)');
      const basicNotification = new Notification(
        notification?.title || data?.title || 'PackMyCart',
        {
          body: notification?.body || data?.body,
          icon: '/logo192.png',
          // No tag to avoid collapsing
        }
      );
      
      basicNotification.onclick = () => {
        window.focus();
        if (data?.click_action) {
          // Handle both absolute and relative URLs
          const clickUrl = data.click_action.startsWith('http') 
            ? data.click_action 
            : window.location.origin + data.click_action;
          window.location.href = clickUrl;
        }
        basicNotification.close();
      };
      
      // Basic notification shown
    }
  }

  /**
   * Check if user has FCM token
   */
  async hasFCMToken(): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      const userRef = doc(db, 'Users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return !!userData.fcmToken;
      }
      return false;
    } catch (error) {
      logger.error('Error checking FCM token', error);
      return false;
    }
  }

  /**
   * Clear FCM token (for logout)
   */
  async clearToken() {
    try {
      const user = auth.currentUser;
      if (!user) {
        // Still clear localStorage even if no user
        this.clearTokenFromLocalStorage();
        return;
      }

      logger.debug(`Attempting to clear FCM token for user: ${user.uid} ${user.email || ''}`);
      
      // Clear localStorage fallback token
      this.clearTokenFromLocalStorage();

      // Find the user document by identity_id
      const usersRef = collection(db, 'Users');
      const userQuery = query(usersRef, where('identity_id', '==', user.uid));
      const querySnapshot = await getDocs(userQuery);

      if (!querySnapshot.empty) {
        // User found by identity_id, update that document
        const userDoc = querySnapshot.docs[0];
        logger.debug('Found user document by identity_id:', userDoc.id);
        await setDoc(doc(db, 'Users', userDoc.id), {
          fcmToken: null,
          fcmTokenUpdatedAt: new Date(),
        }, { merge: true });
        logger.debug('FCM token cleared from existing user document');
        return;
      }

      // Try to find by email if available
      if (user.email) {
        const emailQuery = query(usersRef, where('email', '==', user.email));
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          const userDoc = emailSnapshot.docs[0];
          logger.debug('Found user document by email:', userDoc.id);
          await setDoc(doc(db, 'Users', userDoc.id), {
            fcmToken: null,
            fcmTokenUpdatedAt: new Date(),
          }, { merge: true });
          logger.debug('FCM token cleared from user document found by email');
          return;
        }
      }

      // If no user document found, try to clear using the user's UID as document ID
      try {
        logger.debug('No user document found, attempting to clear with UID as document ID');
        await setDoc(doc(db, 'Users', user.uid), {
          fcmToken: null,
          fcmTokenUpdatedAt: new Date(),
        }, { merge: true });
        logger.debug('FCM token cleared from user document using UID');
      } catch (uidError) {
        logger.error('Failed to clear FCM token using UID as document ID:', uidError);
        logger.warn('No user document found to clear FCM token');
      }
    } catch (error) {
      logger.error('Error clearing FCM token', error);
    }
  }

  /**
   * Refresh FCM token (for expired or invalid tokens)
   */
  async refreshToken(): Promise<string | null> {
    if (!this.isSupported || !messaging) {
      logger.warn('Messaging not supported');
      return null;
    }
    
    try {
      logger.debug('Refreshing FCM token...');
      
      // Delete old token
      await deleteToken(messaging);
      logger.debug('Old token deleted');
      
      // Get new token
      const newToken = await getToken(messaging, { vapidKey: VAPID_KEY });
      
      if (newToken) {
        logger.debug('New FCM token obtained');
        await this.saveTokenToFirestore(newToken);
        logger.debug('FCM token refreshed successfully');
        return newToken;
      } else {
        logger.warn('Failed to obtain new FCM token');
        return null;
      }
      
    } catch (error) {
      logger.error('Error refreshing FCM token', error);
      return null;
    }
  }

  /**
   * Check if token needs refresh (called periodically)
   */
  async checkAndRefreshToken(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Find user document
      const usersRef = collection(db, 'Users');
      const userQuery = query(usersRef, where('identity_id', '==', user.uid));
      const querySnapshot = await getDocs(userQuery);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const tokenUpdatedAt = userData.fcmTokenUpdatedAt;
        
        // Check if token is older than 60 days
        if (tokenUpdatedAt && tokenUpdatedAt.toDate) {
          const tokenAge = Date.now() - tokenUpdatedAt.toDate().getTime();
          const sixtyDaysInMs = 60 * 24 * 60 * 60 * 1000;
          
          if (tokenAge > sixtyDaysInMs) {
            logger.debug('FCM token is older than 60 days, refreshing...');
            await this.refreshToken();
          }
        }
      }
    } catch (error) {
      logger.error('Error checking token age', error);
    }
  }
}

export const fcmService = new FCMService();
